// DashboardPage — aggregate counters from GET /api/v1/dashboard.
// Shows total apps, audits last 30d, and apps at risk (last_wu_pct >= 100).

import { useQuery } from "@tanstack/react-query";
import { AppWindow, ScanLine, AlertTriangle, Zap } from "lucide-react";
import { fetchDashboard, type DashboardData } from "../api";
import { ErrorState } from "@avagency/frontend/components/shell/ErrorState";
import { LoadingState } from "@avagency/frontend/components/shell/LoadingState";
import { EmptyState } from "@avagency/frontend/components/shell/EmptyState";
import { Footer } from "../components/Footer";

interface StatCardProps {
  title: string;
  value: React.ReactNode;
  sub?: string;
  icon: React.ReactNode;
  danger?: boolean;
  accent?: boolean;
}

function StatCard({ title, value, sub, icon, danger, accent }: StatCardProps) {
  const iconColor = danger
    ? "hsl(0 100% 75%)"
    : accent
    ? "hsl(217 91% 65%)"
    : "hsl(218 14% 56%)";

  return (
    <div
      className="rounded-xl border p-5 space-y-3 transition-surface"
      style={{
        background:
          "linear-gradient(180deg, hsl(223 55% 10% / 0.6), hsl(222 67% 5% / 0.8))",
        borderColor: danger
          ? "hsl(0 100% 75% / 0.18)"
          : "hsl(220 18% 85% / 0.1)",
        boxShadow: "0 1px 0 hsl(220 18% 85% / 0.04) inset",
      }}
    >
      <div className="flex items-center justify-between">
        <span
          className="text-xs font-medium uppercase tracking-wider"
          style={{
            color: "hsl(218 14% 56%)",
            letterSpacing: "0.1em",
            fontFamily: "var(--font-mono, ui-monospace)",
          }}
        >
          {title}
        </span>
        <span aria-hidden="true" style={{ color: iconColor }}>
          {icon}
        </span>
      </div>
      <div>
        <div
          className="text-3xl font-semibold"
          style={{
            letterSpacing: "-0.03em",
            color: danger ? "hsl(0 100% 78%)" : "hsl(220 18% 90%)",
          }}
        >
          {value}
        </div>
        {sub && (
          <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
            {sub}
          </p>
        )}
      </div>
    </div>
  );
}

function CounterCards({ d }: { d: DashboardData }) {
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <StatCard
        title="Total Apps"
        value={d.total_apps}
        sub="Bubble apps connected"
        icon={<AppWindow className="h-4 w-4" />}
        accent
      />
      <StatCard
        title="Audits (30d)"
        value={d.audits_last_30d}
        sub="WU audits in last 30 days"
        icon={<ScanLine className="h-4 w-4" />}
      />
      <StatCard
        title="At Risk"
        value={d.apps_at_risk}
        sub="Apps at or over WU plan limit"
        icon={<AlertTriangle className="h-4 w-4" />}
        danger={d.apps_at_risk > 0}
      />
    </div>
  );
}

export function DashboardPage() {
  const q = useQuery<DashboardData>({
    queryKey: ["bubble", "dashboard"],
    queryFn: fetchDashboard,
  });

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1
          className="text-2xl font-semibold"
          style={{ letterSpacing: "-0.02em" }}
        >
          Dashboard
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Workflow Unit usage overview across your Bubble apps.
        </p>
      </div>

      {q.isLoading && <LoadingState rows={3} />}

      {q.error && (
        <ErrorState
          error={q.error}
          retry={() => q.refetch()}
          description="Could not load dashboard stats. Make sure your Bubble integration is active."
        />
      )}

      {q.data && q.data.total_apps === 0 && (
        <EmptyState
          icon={<Zap className="h-10 w-10 opacity-50" />}
          title="You're all set — waiting on your first sync"
          description="Your Bubble integration is connected. Apps and WU data will appear here automatically after the first sync runs (usually within a minute)."
        />
      )}

      {q.data && q.data.total_apps > 0 && <CounterCards d={q.data} />}

      <Footer />
    </div>
  );
}
